type ModifierMouseEvent = Pick<
  MouseEvent,
  'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'button'
>;

/**
 * True when a click should keep the current page alive (open in a new
 * tab/window) — modifier keys or a non-primary mouse button. In that case the
 * page is not unloading, so analytics fire normally and navigation must not be
 * deferred or hijacked.
 */
export const isModifiedClick = (e: ModifierMouseEvent): boolean =>
  e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;

/**
 * Wraps `fn` so it runs at most once. Used to guard deferred-navigation
 * callbacks, which can be invoked both by GTM and by our own timeout fallback.
 */
export const once = (fn: () => void): (() => void) => {
  let called = false;
  return () => {
    if (called) return;
    called = true;
    fn();
  };
};

export const toRelativeUrl = (href: string): string => {
  const url = new URL(href, window.location.origin);
  return `${url.pathname}${url.search}`;
};

export interface DeferredNavigationProps {
  /**
   * Invoked once the GTM event has been dispatched (or after `eventTimeout`
   * ms, plus a small buffer for GTM's own timeout handling, as a fallback). Use
   * this to defer navigation so the analytics hit is not lost when the page unloads.
   */
  eventCallback?: () => void;
  /** Max time (ms) to wait for the event before running `eventCallback`. */
  eventTimeout?: number;
}

/**
 * Builds the `eventCallback`/`eventTimeout` pair GTM uses to signal that all
 * tags for an event have fired, and arms the fallback timer that guarantees the
 * caller still navigates when GTM is blocked and never calls back. Returns an
 * empty object when the caller is not deferring navigation, so those keys stay
 * off the dataLayer push entirely.
 */
export const deferredNavigationFields = ({
  eventCallback,
  eventTimeout = 500,
}: DeferredNavigationProps) => {
  if (!eventCallback) return {};

  const onSent = once(eventCallback);

  setTimeout(onSent, eventTimeout + 250);

  return { eventCallback: onSent, eventTimeout };
};
